import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, Clock, Package, Truck, CircleCheck, Ban, Home, Copy, Check, RefreshCw, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import { useOrder } from '@/hooks/useOrder'
import { useOrg } from '@/context/OrgContext'
import { formatRupiah } from '@/lib/utils'
import Skeleton from '@/components/Skeleton'
import type { OrderStatusId } from '@/types'

const STATUS_CONFIG: Record<OrderStatusId, { label: string; icon: typeof Clock; color: string }> = {
  1: { label: 'Menunggu Konfirmasi', icon: Clock,       color: 'text-yellow-500' },
  2: { label: 'Dikonfirmasi',        icon: CheckCircle, color: 'text-blue-500'   },
  3: { label: 'Selesai',             icon: CircleCheck, color: 'text-green-500'  },
  4: { label: 'Dibatalkan',          icon: Ban,         color: 'text-red-500'    },
  5: { label: 'Dipinjam',            icon: Truck,       color: 'text-purple-500' },
  6: { label: 'Dikembalikan',        icon: CircleCheck, color: 'text-teal-500'   },
}

function OrderIdBadge({ nomor, id }: { nomor: string | null; id: string }) {
  const [copied, setCopied] = useState(false)
  const display = nomor ?? `#${id.slice(0, 8).toUpperCase()}`

  function copy() {
    navigator.clipboard.writeText(nomor ?? id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={copy}
      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
    >
      <span className="font-mono font-semibold tracking-wide">{display}</span>
      {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
    </button>
  )
}

function buildWaMessage(order: NonNullable<ReturnType<typeof useOrder>['order']>) {
  const tanggal = new Date(order.tanggal_kirim).toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  })
  const items = (order.order_items ?? [])
    .map(i => `- ${i.nama_produk} x${i.qty} (${formatRupiah(i.subtotal)})`)
    .join('\n')
  const nomor = order.nomor_order ?? `#${order.id.slice(0, 8).toUpperCase()}`

  return `Halo, saya sudah pesan melalui website.\n\n*Order ${nomor}*\n${items}\n\n📅 Tanggal: ${tanggal}, pukul ${order.jam_kirim}\n💳 Pembayaran: ${order.metode_bayar === 'tunai' ? 'Tunai' : 'Transfer Bank'}\n\nMohon konfirmasinya, terima kasih!`
}

export default function OrderSelesai() {
  const [params] = useSearchParams()
  const orderId = params.get('order_id') ?? ''
  const { order, loading, notFound } = useOrder(orderId)
  const { org } = useOrg()

  if (!orderId) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-gray-400">
        <p>Tidak ada order yang dicek.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-blue-600 hover:underline">
          Kembali ke beranda
        </Link>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    )
  }

  if (notFound || !order) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-gray-400">
        <p className="font-medium">Pesanan tidak ditemukan.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-blue-600 hover:underline">
          Kembali ke beranda
        </Link>
      </div>
    )
  }

  const status = STATUS_CONFIG[order.status_id]
  const StatusIcon = status.icon
  const isNew = order.status_id === 1

  return (
    <div className="max-w-xl mx-auto px-4 py-8 pb-12">

      {/* Success banner */}
      <div className={`rounded-2xl p-6 text-center mb-6 ${
        isNew ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-100'
      }`}>
        {isNew ? (
          <>
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={28} className="text-green-500" />
            </div>
            <h1 className="text-lg font-bold text-gray-900">Pesanan Diterima!</h1>
            <p className="text-sm text-gray-500 mt-1">
              Kami akan segera memproses pesanan Anda.
            </p>
          </>
        ) : (
          <>
            <div className={`w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3`}>
              <StatusIcon size={28} className={status.color} />
            </div>
            <h1 className="text-lg font-bold text-gray-900">{status.label}</h1>
          </>
        )}
        <div className="mt-3">
          <OrderIdBadge nomor={order.nomor_order} id={order.id} />
        </div>
      </div>

      {/* Detail pesanan */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Detail Pesanan</p>

        {/* Produk */}
        {order.order_items && order.order_items.length > 0 ? (
          <>
            {order.order_items.map((item) => (
              <div key={item.id} className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{item.nama_produk}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatRupiah(item.harga_satuan)} × {item.qty}
                  </p>
                </div>
                <p className="font-bold text-gray-900 text-sm">{formatRupiah(item.subtotal)}</p>
              </div>
            ))}
            <div className="border-t border-gray-100" />
          </>
        ) : (
          <div className="text-sm text-gray-400">Tidak ada item</div>
        )}

        {/* Pengiriman */}
        <div className="space-y-2 text-sm">
          <div className="flex gap-2">
            <Package size={15} className="text-gray-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-700">{order.nama_pemesan}</p>
              <p className="text-gray-400 text-xs">{order.alamat}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Truck size={15} className="text-gray-400 shrink-0 mt-0.5" />
            <p className="text-gray-600">
              {new Date(order.tanggal_kirim).toLocaleDateString('id-ID', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
              })}, pukul {order.jam_kirim}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Pembayaran */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-500">Pembayaran</span>
          <span className="font-medium text-gray-800 capitalize">
            {order.metode_bayar === 'tunai' ? 'Bayar Tunai' : 'Transfer Bank'}
          </span>
        </div>

        {/* Status */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-500">Status</span>
          <span className={`font-semibold ${status.color}`}>{status.label}</span>
        </div>
      </div>

      {/* Order Lagi — hanya tampil jika status Selesai dan ada produk */}
      {order.status_id === 3 && order.produk_id && (
        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-800">Pesan lagi?</p>
            <p className="text-xs text-blue-500 mt-0.5">{order.nama_produk}</p>
          </div>
          <Link
            to={`/order?produk_id=${order.produk_id}`}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors shrink-0"
          >
            <RefreshCw size={14} />
            Order Lagi
          </Link>
        </div>
      )}

      {/* Konfirmasi WA */}
      {org?.no_telp && (
        <a
          href={`https://wa.me/${org.no_telp.replace(/\D/g, '').replace(/^0/, '62')}?text=${encodeURIComponent(buildWaMessage(order))}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
        >
          <MessageCircle size={16} />
          Konfirmasi via WhatsApp
        </a>
      )}

      {/* Back to home */}
      <Link
        to="/"
        className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <Home size={16} />
        Kembali ke Beranda
      </Link>
    </div>
  )
}
